import { useRef } from 'react'
import { flushSync } from 'react-dom'

import { Trash } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { PlacementId } from '../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { EmbroideryTypeKey } from '../../../../../containers/EmbroideryContainer/combination-rules/types'
import { useEmbroideryGarment } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryGarmentProvider'
import { useEmbroiderySelection } from '../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import { EmbroideryItem } from '../../../../../containers/EmbroideryContainer/providers/types'
import { isLightProductColor } from '../../../../../containers/EmbroideryContainer/utils/unorganized'
import { FOCUSABLE } from '../../../../../lib/utils/focusable'
import { placementName } from '../PlacementPicker/placementLabels'

import {
  OccupantContent,
  OccupantDividers,
  OccupantHeader,
  OccupantImage,
  OccupantLabel,
  OccupantList,
  OccupantRow,
  OccupantText,
  OccupantTile,
  OccupantTrash,
} from './styles'
import { TextGlyph } from './TextGlyph'

const firstFocusable = (element: Element | null) =>
  element?.matches(FOCUSABLE) ? element : element?.querySelector(FOCUSABLE)

const OCCUPANT_ORDER: readonly EmbroideryTypeKey[] = ['text', 'logo', 'icon']

export type PlacementOccupantsProps = {
  /** The editor's own type, which is left out: its items are what the editor itself is showing. */
  typeKey: EmbroideryTypeKey
  placementId: PlacementId | undefined
  /** Which sides of the list get a rule, so it doesn't double one the neighboring section draws. */
  dividers: OccupantDividers
}

/**
 * The embroidery of other types already at a placement, each with a button that removes it. Lets
 * the customer see and clear what they are adding next to without leaving the editor.
 */
export const PlacementOccupants: React.FC<PlacementOccupantsProps> = ({
  typeKey,
  placementId,
  dividers,
}) => {
  const { items, removeItem } = useEmbroiderySelection()
  const listRef = useRef<HTMLDivElement>(null)
  const { productColorHex } = useEmbroideryGarment()
  const {
    embroidery: {
      optionRows: { typeLabels },
      placement: copy,
    },
  } = useTranslation()

  if (placementId === undefined) return null

  const occupants = items
    .filter(item => item.placementId === placementId && item.typeKey !== typeKey)
    .sort((a, b) => OCCUPANT_ORDER.indexOf(a.typeKey) - OCCUPANT_ORDER.indexOf(b.typeKey))

  if (occupants.length === 0) return null

  // The row's trash button unmounts with the row, so focus moves to the next occupant's button.
  // With none left, the list unmounts too, so it moves to the nearest field beside where it was.
  const removeOccupant = (instanceId: string, index: number) => {
    const list = listRef.current
    const nextSibling = list?.nextElementSibling ?? null
    const previousSibling = list?.previousElementSibling ?? null

    flushSync(() => removeItem(instanceId))

    const remaining = listRef.current?.querySelectorAll('button')
    const target = remaining?.length
      ? remaining[Math.min(index, remaining.length - 1)]
      : firstFocusable(nextSibling) ?? firstFocusable(previousSibling)
    if (target instanceof HTMLElement) target.focus()
  }

  const tileColor = productColorHex ?? 'white'
  const glyphColor = isLightProductColor(tileColor) ? 'black' : 'white'

  return (
    <OccupantList ref={listRef} $dividers={dividers}>
      {occupants.map((item, index) => {
        const label = typeLabels[item.typeKey]

        return (
          <OccupantRow key={item.instanceId}>
            <OccupantHeader>
              <OccupantLabel as='span'>{label}</OccupantLabel>
              <OccupantTrash
                type='button'
                aria-label={copy.removeOccupant
                  .replace('{{type}}', label)
                  .replace('{{placement}}', placementName(placementId))}
                onClick={() => removeOccupant(item.instanceId, index)}
              >
                <Trash height='16px' width='16px' />
              </OccupantTrash>
            </OccupantHeader>

            <OccupantContent>
              <OccupantTile $backgroundColor={tileColor} $color={glyphColor}>
                <OccupantArtwork item={item} />
              </OccupantTile>
              <OccupantText>
                <OccupantDescription item={item} />
              </OccupantText>
            </OccupantContent>
          </OccupantRow>
        )
      })}
    </OccupantList>
  )
}

const ARTWORK_SIZE = 25

const OccupantArtwork: React.FC<{ item: EmbroideryItem }> = ({ item }) => {
  const src =
    item.typeKey === 'icon'
      ? item.values.icon?.image.src
      : item.typeKey === 'logo'
      ? item.values.logoFile?.preview
      : undefined

  if (item.typeKey === 'text') return <TextGlyph />
  if (src === undefined) return null

  // The name beside the tile already says what it is, so the image itself is decorative.
  return (
    <OccupantImage
      src={src}
      alt=''
      widths={{ unit: 'px', sm: ARTWORK_SIZE, md: ARTWORK_SIZE }}
      aspectRatios={{ sm: 1, md: 1 }}
      loading='default'
      transparentBackground
      $grayscale={
        item.typeKey === 'logo' && item.values.logoFile?.digitizationSetting === 'BLACK_WHITE'
      }
    />
  )
}

const OccupantDescription: React.FC<{ item: EmbroideryItem }> = ({ item }) => {
  switch (item.typeKey) {
    case 'text':
      return <>{[item.values.firstLine, item.values.secondLine].filter(Boolean).join('\n')}</>
    case 'icon':
      return <>{item.values.icon?.image.label}</>
    case 'logo':
      return <>{item.values.logoFile?.name}</>
  }
}
